import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { SignatureList } from './SignatureList';
import { CreateSignatureModal } from './CreateSignatureModal';
import { useSignatures } from '../../../../../hooks/useSignatures';
import type {
  UserSignature,
  SystemSignature,
} from '../../../../../store/reducers/signatures.store';

export const EmailSignatures: React.FC = () => {
  const [editingSignature, setEditingSignature] = useState<
    UserSignature | SystemSignature | null
  >(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const {
    signatures,
    loading,
    error,
    operationLoading,
    createSignature,
    updateSignature,
    deleteSignature,
    setDefaultSignature,
    clearErrors,
  } = useSignatures();

  const handleCreateSignature = async (signatureData: {
    name: string;
    content: string;
    isDefault: boolean;
  }) => {
    await createSignature(signatureData);
  };

  const handleUpdateSignature = async (signatureData: {
    name: string;
    content: string;
    isDefault: boolean;
  }) => {
    if (editingSignature) {
      await updateSignature(editingSignature.id, signatureData);
      setEditingSignature(null);
    }
  };

  const handleEditSignature = (signature: UserSignature | SystemSignature) => {
    setEditingSignature(signature);
    setIsCreateModalOpen(true);
  };

  const handleDeleteSignature = async (id: string) => {
    try {
      await deleteSignature(id);
    } catch (error) {
      console.error('EmailSignatures: deleteSignature failed:', error);
    }
  };

  const handleSetDefaultSignature = async (id: string) => {
    await setDefaultSignature(id);
  };

  const handleModalClose = () => {
    setIsCreateModalOpen(false);
    setEditingSignature(null);
  };

  const handleModalSave = editingSignature
    ? handleUpdateSignature
    : handleCreateSignature;

  return (
    <Card style={{ height: '100%' }}>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Box>
            <Typography variant="h6" gutterBottom>
              Email Signatures
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your personal email signatures
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsCreateModalOpen(true)}
            disabled={operationLoading}
          >
            Create Signature
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => clearErrors()}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <SignatureList
            signatures={signatures}
            onEdit={handleEditSignature}
            onDelete={handleDeleteSignature}
            onSetDefault={handleSetDefaultSignature}
            loading={operationLoading}
          />
        )}

        <CreateSignatureModal
          open={isCreateModalOpen}
          onClose={handleModalClose}
          onSave={handleModalSave}
          editingSignature={editingSignature}
          loading={operationLoading}
        />
      </CardContent>
    </Card>
  );
};
